<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useSocialCommentsStore } from 'dashboard/stores/socialComments';
import { useAlert } from 'dashboard/composables';
import CommentActionsBar from './CommentActionsBar.vue';

const props = defineProps({
  comment: { type: Object, default: null },
});

const { t } = useI18n();

const commentsStore = useSocialCommentsStore();
const { threadRecords, uiFlags } = storeToRefs(commentsStore);

// Moderation buttons disable together while any action on the selected comment is
// in flight -- these flags are store-global rather than per-comment, but only one
// comment's actions bar is ever mounted at a time, so that is not a distinction the
// UI needs to make.
const isActionInFlight = computed(
  () =>
    uiFlags.value.hiding ||
    uiFlags.value.unhiding ||
    uiFlags.value.liking ||
    uiFlags.value.unliking ||
    uiFlags.value.handling ||
    uiFlags.value.deletingItem
);

// Every one of these is a Graph write that can fail -- an expired page token, or a comment
// Facebook has removed since the queue was fetched. The store rethrows Meta's own message, so
// surface that: unhandled, the rejection left the bar looking as though it had simply done
// nothing, and the comment silently unmoderated.
const runAction = async action => {
  try {
    await action();
  } catch (error) {
    useAlert(error.message || t('COMMENTS.ACTIONS.ERROR'));
  }
};

const handleHide = () => runAction(() => commentsStore.hide(props.comment.id));
const handleUnhide = () =>
  runAction(() => commentsStore.unhide(props.comment.id));
const handleDelete = () =>
  runAction(() => commentsStore.delete(props.comment.id));
const handleLike = () => runAction(() => commentsStore.like(props.comment.id));
const handleUnlike = () =>
  runAction(() => commentsStore.unlike(props.comment.id));
const handleMarkHandled = () =>
  runAction(() => commentsStore.handle(props.comment.id));

// The comments API returns a flat list of sibling rows rather than a nested reply
// tree, and the default queue filter (`needs_reply`) excludes the page's own
// replies by definition -- they are already handled, not awaiting one. So the
// page's own replies to this comment can never be found in the queue's `records`;
// they have to come from a dedicated thread fetch (`threadRecords`, scoped by
// social post and `state: 'all'`) kept entirely separate from the queue list.
const pageReplies = computed(() => {
  if (!props.comment) return [];
  return threadRecords.value
    .filter(
      record =>
        record.fromPage && record.parentExternalId === props.comment.externalId
    )
    .sort((a, b) => (a.commentedAt || '').localeCompare(b.commentedAt || ''));
});

// Re-fetch whenever the selected comment's post changes so the pane never shows a
// previous post's thread while the new one loads. clearThread() runs synchronously
// before the fetch so stale replies never linger mid-flight, and the fetch failure
// is swallowed here (not re-thrown) so a Graph error on the thread call doesn't take
// down the rest of the detail pane.
watch(
  () => props.comment?.socialPost?.id,
  socialPostId => {
    commentsStore.clearThread();
    if (socialPostId) {
      commentsStore.fetchThread(socialPostId).catch(() => {});
    }
  },
  { immediate: true }
);

const relativeTime = value => {
  if (!value) return t('COMMENTS.LIST.NO_DATE');
  return formatDistanceToNow(parseISO(value), { addSuffix: true });
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto">
    <div
      v-if="!comment"
      data-testid="comment-detail-empty"
      class="flex items-center justify-center flex-1 p-6 text-sm text-center text-n-slate-10"
    >
      {{ t('COMMENTS.DETAIL.EMPTY') }}
    </div>
    <template v-else>
      <div class="flex items-start gap-3 p-4 border-b border-n-weak">
        <img
          v-if="comment.socialPost?.pictureUrl"
          :src="comment.socialPost.pictureUrl"
          alt=""
          class="object-cover rounded size-12 shrink-0"
        />
        <div class="flex flex-col min-w-0 gap-1">
          <p class="text-sm text-n-slate-12">
            {{ comment.socialPost?.message }}
          </p>
          <a
            v-if="comment.socialPost?.permalink"
            :href="comment.socialPost.permalink"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="post-permalink"
            class="text-xs text-n-blue-9 hover:underline"
          >
            {{ t('COMMENTS.DETAIL.VIEW_POST') }}
          </a>
        </div>
      </div>

      <div
        data-testid="comment-detail-comment"
        class="flex flex-col gap-1 p-4 border-b border-n-weak"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ comment.authorName }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ relativeTime(comment.commentedAt) }}
          </span>
        </div>
        <p class="text-sm text-n-slate-11">{{ comment.message }}</p>
      </div>

      <CommentActionsBar
        :comment="comment"
        :is-loading="isActionInFlight"
        @hide="handleHide"
        @unhide="handleUnhide"
        @delete="handleDelete"
        @like="handleLike"
        @unlike="handleUnlike"
        @handle="handleMarkHandled"
      />

      <div
        v-if="pageReplies.length"
        class="flex flex-col gap-3 p-4 border-b border-n-weak"
      >
        <p class="text-xs font-medium tracking-wide uppercase text-n-slate-10">
          {{ t('COMMENTS.DETAIL.REPLIES_HEADING') }}
        </p>
        <div
          v-for="reply in pageReplies"
          :key="reply.id"
          data-testid="comment-detail-reply"
          class="flex flex-col gap-1 py-1 pl-3 border-l-2 border-n-weak"
        >
          <div class="flex items-center justify-between gap-2">
            <div class="flex items-center min-w-0 gap-1.5">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ reply.authorName }}
              </span>
              <span
                data-testid="page-reply-badge"
                class="px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded shrink-0 bg-n-blue-3 text-n-blue-11"
              >
                {{ t('COMMENTS.DETAIL.PAGE_BADGE') }}
              </span>
            </div>
            <span class="text-xs text-n-slate-10 shrink-0">
              {{ relativeTime(reply.commentedAt) }}
            </span>
          </div>
          <p class="text-sm text-n-slate-11">{{ reply.message }}</p>
        </div>
      </div>
    </template>
  </div>
</template>
